import React, { useState, useEffect, useRef, useCallback } from 'react';
import { authService, chatService } from '../services/api';
import { CHAT_MENSAJE_MAX_CHARS } from '../constants/uploadLimits';
import ConfirmPopup from '../components/ConfirmPopup';
import './Chat.css';

/** Intervalo para traer mensajes nuevos (sin WebSockets). */
const POLL_MENSAJES_MS = 10000;

const firmaListaMensajes = (lista) => (Array.isArray(lista) ? lista.map((m) => m.id).join(',') : '');

const Chat = () => {
  const [userName, setUserName] = useState('');
  const [userRole, setUserRole] = useState('');
  const [salas, setSalas] = useState([]);
  const [loadingSalas, setLoadingSalas] = useState(true);
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messageDraft, setMessageDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [cursosOpen, setCursosOpen] = useState(false);
  const [gruposOpen, setGruposOpen] = useState(false);
  const [onlineCount, setOnlineCount] = useState(0);
  const [showOnlineModal, setShowOnlineModal] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [loadingOnline, setLoadingOnline] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [moderacion, setModeracion] = useState(null);
  const [loadingModeracion, setLoadingModeracion] = useState(false);
  const [savingModeracion, setSavingModeracion] = useState(false);
  const messagesEndRef = useRef(null);

  const general = salas.filter((s) => s.tipo === 'general');
  const cursos = salas.filter((s) => s.tipo === 'curso');
  const grupos = salas.filter((s) => s.tipo === 'grupo');
  const selectedRoom = salas.find((r) => r.id === selectedRoomId);

  const esEquipo = ['Coordinador', 'Profesor', 'Tutor'].includes(userRole);
  const soloEquipoPuedeEscribir = selectedRoom?.solo_equipo_puede_escribir || moderacion?.solo_equipo_puede_escribir || false;
  const estoySilenciado = (moderacion?.usuarios_silenciados || []).some((u) => Number(u.id) === Number(currentUserId));
  const puedoEscribir = (!soloEquipoPuedeEscribir || esEquipo) && !estoySilenciado;

  // Nombre, rol y id del usuario
  useEffect(() => {
    const user = authService.getCurrentUser();
    setCurrentUserId(user?.id ?? null);
    const name = user?.first_name?.trim() || user?.username || 'Usuario';
    setUserName(name);
    const savedRole = localStorage.getItem('selectedRole');
    const role = savedRole || (Array.isArray(user?.groups) && user.groups[0]) || (user?.groups?.name) || 'Estudiante';
    setUserRole(typeof role === 'string' ? role : (role?.name || 'Estudiante'));
  }, []);
  useEffect(() => {
    const handleRoleChange = () => {
      const r = localStorage.getItem('selectedRole');
      if (r) setUserRole(r);
    };
    window.addEventListener('roleChanged', handleRoleChange);
    return () => window.removeEventListener('roleChanged', handleRoleChange);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingSalas(true);
      setError('');
      try {
        const data = await chatService.getSalas();
        if (!cancelled) {
          setSalas(Array.isArray(data) ? data : []);
          if (data.length > 0 && !selectedRoomId) setSelectedRoomId(data[0].id);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || err.response?.data?.detail || 'No se pudieron cargar las salas');
          setSalas([]);
        }
      } finally {
        if (!cancelled) setLoadingSalas(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (selectedRoomId == null) return;
    let cancelled = false;
    setLoadingMessages(true);
    setError('');
    chatService.getMensajes(selectedRoomId)
      .then((data) => {
        if (!cancelled) setMessages(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.error || err.response?.data?.detail || 'No se pudieron cargar los mensajes');
          setMessages([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingMessages(false);
      });
    return () => { cancelled = true; };
  }, [selectedRoomId]);

  /** Refresca mensajes desde el servidor; solo actualiza estado si cambió (evita scroll innecesario). */
  const refrescarMensajes = useCallback(async (salaId) => {
    if (salaId == null) return;
    try {
      const data = await chatService.getMensajes(salaId);
      const arr = Array.isArray(data) ? data : [];
      setMessages((prev) => {
        if (firmaListaMensajes(prev) === firmaListaMensajes(arr)) return prev;
        return arr;
      });
    } catch {
      /* no pisar lista local si falla un poll */
    }
  }, []);

  // Polling: que todos vean mensajes nuevos sin recargar la página
  useEffect(() => {
    if (selectedRoomId == null) return undefined;
    let cancelled = false;

    const pollSiVisible = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      if (!cancelled) refrescarMensajes(selectedRoomId);
    };

    const id = setInterval(pollSiVisible, POLL_MENSAJES_MS);

    const onVis = () => {
      if (document.visibilityState === 'visible') pollSiVisible();
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [selectedRoomId, refrescarMensajes]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Heartbeat y conteo de online en la sala actual
  useEffect(() => {
    if (selectedRoomId == null) {
      setOnlineCount(0);
      return;
    }
    const tick = async () => {
      try {
        await chatService.heartbeat(selectedRoomId);
        const data = await chatService.getUsuariosOnline(selectedRoomId);
        setOnlineCount(data.count ?? 0);
      } catch {
        setOnlineCount(0);
      }
    };
    tick();
    const id = setInterval(tick, 25000);
    return () => clearInterval(id);
  }, [selectedRoomId]);

  // Cargar moderación cuando el equipo entra a una sala
  useEffect(() => {
    if (!selectedRoomId || !esEquipo) {
      setModeracion(null);
      return;
    }
    let cancelled = false;
    setLoadingModeracion(true);
    chatService.getModeracion(selectedRoomId)
      .then((data) => { if (!cancelled) setModeracion(data); })
      .catch(() => { if (!cancelled) setModeracion(null); })
      .finally(() => { if (!cancelled) setLoadingModeracion(false); });
    return () => { cancelled = true; };
  }, [selectedRoomId, esEquipo]);

  const handleToggleSoloEquipo = async () => {
    if (!selectedRoomId || !esEquipo || savingModeracion) return;
    setSavingModeracion(true);
    setError('');
    try {
      const data = await chatService.patchModeracion(selectedRoomId, { solo_equipo_puede_escribir: !soloEquipoPuedeEscribir });
      setModeracion(data);
      setSalas((prev) => prev.map((s) => s.id === selectedRoomId ? { ...s, solo_equipo_puede_escribir: data.solo_equipo_puede_escribir } : s));
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo actualizar');
    } finally {
      setSavingModeracion(false);
    }
  };

  const handleSilenciarUsuario = async (userId) => {
    if (!selectedRoomId || !esEquipo) return;
    setError('');
    try {
      await chatService.silenciarUsuario(selectedRoomId, userId);
      const data = await chatService.getModeracion(selectedRoomId);
      setModeracion(data);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo silenciar');
    }
  };

  const handleDesilenciarUsuario = async (userId) => {
    if (!selectedRoomId || !esEquipo) return;
    setError('');
    try {
      await chatService.desilenciarUsuario(selectedRoomId, userId);
      const data = await chatService.getModeracion(selectedRoomId);
      setModeracion(data);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo quitar el silencio');
    }
  };

  const handleOcultarMensaje = async (mensajeId) => {
    if (!selectedRoomId || !esEquipo) return;
    setError('');
    try {
      await chatService.ocultarMensaje(selectedRoomId, mensajeId);
      setMessages((prev) => prev.filter((m) => m.id !== mensajeId));
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo ocultar el mensaje');
    }
  };

  const openOnlineModal = async () => {
    if (!selectedRoomId) return;
    setShowOnlineModal(true);
    setLoadingOnline(true);
    try {
      const data = await chatService.getUsuariosOnline(selectedRoomId);
      setOnlineUsers(data.usuarios ?? []);
    } catch {
      setOnlineUsers([]);
    } finally {
      setLoadingOnline(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const text = (messageDraft || '').trim();
    if (!text || !selectedRoomId || sending) return;
    if (text.length > CHAT_MENSAJE_MAX_CHARS) {
      setError(`El mensaje no puede superar ${CHAT_MENSAJE_MAX_CHARS.toLocaleString('es-AR')} caracteres.`);
      return;
    }
    setSending(true);
    setError('');
    try {
      const newMsg = await chatService.enviarMensaje(selectedRoomId, text, userRole);
      setMessageDraft('');
      setMessages((prev) => [...prev, newMsg]);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.detail || 'No se pudo enviar el mensaje');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="chat-page">
      <ConfirmPopup
        open={Boolean(error)}
        title="Error"
        message={error}
        confirmLabel="Aceptar"
        cancelLabel={null}
        onConfirm={() => setError('')}
        onCancel={() => setError('')}
        variant="danger"
      />
      <aside className="chat-rooms">
        <div className="chat-rooms-header">
          <h2>Chat</h2>
          <p className="chat-user-rol" aria-label={`Conectado como ${userName}, rol ${userRole}`}>
            {userName}
            {userRole && <span className="chat-user-rol-badge">{userRole}</span>}
          </p>
        </div>
        {loadingSalas ? (
          <p className="chat-rooms-hint">Cargando salas…</p>
        ) : salas.length === 0 ? (
          <p className="chat-rooms-hint">No hay salas disponibles.</p>
        ) : (
          <>
            {general.length > 0 && (
              <section className="chat-rooms-section">
                <h3>General</h3>
                <ul>
                  {general.map((room) => (
                    <li key={room.id}>
                      <button
                        type="button"
                        className={`chat-room-item ${selectedRoomId === room.id ? 'active' : ''}`}
                        onClick={() => setSelectedRoomId(room.id)}
                      >
                        <span className="chat-room-icon">{room.icon || '🌐'}</span>
                        <span className="chat-room-name">{room.nombre}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {cursos.length > 0 && (
              <section className={`chat-rooms-section chat-rooms-section--collapsible ${cursosOpen ? 'is-open' : ''}`}>
                <button
                  type="button"
                  className="chat-rooms-section-toggle"
                  onClick={() => setCursosOpen((o) => !o)}
                  aria-expanded={cursosOpen}
                >
                  <h3>Por curso</h3>
                  <span className="chat-rooms-section-chevron" aria-hidden>{cursosOpen ? '▼' : '▶'}</span>
                </button>
                {cursosOpen && (
                  <>
                    <p className="chat-rooms-hint">Salas de los cursos en los que estás inscrito</p>
                    <ul className="chat-rooms-list-scroll">
                      {cursos.map((room) => (
                        <li key={room.id}>
                          <button
                            type="button"
                            className={`chat-room-item ${selectedRoomId === room.id ? 'active' : ''}`}
                            onClick={() => setSelectedRoomId(room.id)}
                          >
                            <span className="chat-room-icon">{room.icon || '📚'}</span>
                            <span className="chat-room-name">{room.nombre}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </section>
            )}
            {grupos.length > 0 && (
              <section className={`chat-rooms-section chat-rooms-section--collapsible ${gruposOpen ? 'is-open' : ''}`}>
                <button
                  type="button"
                  className="chat-rooms-section-toggle"
                  onClick={() => setGruposOpen((o) => !o)}
                  aria-expanded={gruposOpen}
                >
                  <h3>Grupos</h3>
                  <span className="chat-rooms-section-chevron" aria-hidden>{gruposOpen ? '▼' : '▶'}</span>
                </button>
                {gruposOpen && (
                  <>
                    <p className="chat-rooms-hint">Salas de los grupos de entregas</p>
                    <ul className="chat-rooms-list-scroll">
                      {grupos.map((room) => (
                        <li key={room.id}>
                          <button
                            type="button"
                            className={`chat-room-item ${selectedRoomId === room.id ? 'active' : ''}`}
                            onClick={() => setSelectedRoomId(room.id)}
                          >
                            <span className="chat-room-icon">{room.icon || '👥'}</span>
                            <span className="chat-room-name">{room.nombre}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </section>
            )}
            {salas.length === 0 && !error && (
              <p className="chat-rooms-hint">No hay salas disponibles. Crea una sala General desde el admin o inscríbete a un curso.</p>
            )}
          </>
        )}
      </aside>

      <main className="chat-main">
        {selectedRoom ? (
          <>
            <header className="chat-header">
              <span className="chat-header-icon">{selectedRoom.icon || '💬'}</span>
              <div className="chat-header-title">
                <h1>{selectedRoom.nombre}</h1>
                <span className="chat-header-meta">
                  {selectedRoom.tipo === 'general' && 'Todos los usuarios'}
                  {selectedRoom.tipo === 'curso' && 'Sala del curso'}
                  {selectedRoom.tipo === 'grupo' && 'Grupo de entrega'}
                </span>
              </div>
              <button
                type="button"
                className="chat-header-online"
                onClick={openOnlineModal}
                title="Ver conectados"
              >
                <span className="chat-header-online-dot" aria-hidden />
                {onlineCount} en línea
              </button>
            </header>

            {soloEquipoPuedeEscribir && (
              <div className="chat-banner-silenciado" role="status">
                Solo Coordinador, Profesor y Tutor pueden enviar mensajes en esta sala.
              </div>
            )}
            {estoySilenciado && (
              <div className="chat-banner-yo-silenciado" role="alert">
                No podés enviar mensajes en esta sala (estás silenciado).
              </div>
            )}

            {esEquipo && selectedRoomId && (
              <div className="chat-moderacion">
                <div className="chat-moderacion-row">
                  <label className="chat-moderacion-toggle">
                    <input
                      type="checkbox"
                      checked={soloEquipoPuedeEscribir}
                      disabled={savingModeracion || loadingModeracion}
                      onChange={handleToggleSoloEquipo}
                    />
                    <span>Solo equipo puede escribir</span>
                  </label>
                </div>
                {(moderacion?.usuarios_silenciados?.length || 0) > 0 && (
                  <div className="chat-moderacion-silenciados">
                    <span className="chat-moderacion-label">Usuarios silenciados:</span>
                    <ul className="chat-moderacion-silenciados-list">
                      {moderacion.usuarios_silenciados.map((u) => (
                        <li key={u.id} className="chat-moderacion-silenciados-item">
                          <span>{u.nombre || u.username}</span>
                          <button
                            type="button"
                            className="chat-moderacion-btn-desilenciar"
                            onClick={() => handleDesilenciarUsuario(u.id)}
                          >
                            Quitar silencio
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {showOnlineModal && (
              <div className="chat-online-modal-overlay" onClick={() => setShowOnlineModal(false)} role="dialog" aria-modal="true" aria-label="Usuarios conectados">
                <div className="chat-online-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="chat-online-modal-header">
                    <h3>Conectados en {selectedRoom?.nombre}</h3>
                    <button type="button" className="chat-online-modal-close" onClick={() => setShowOnlineModal(false)} aria-label="Cerrar">✕</button>
                  </div>
                  <div className="chat-online-modal-body">
                    {loadingOnline ? (
                      <p className="chat-rooms-hint">Cargando…</p>
                    ) : onlineUsers.length === 0 ? (
                      <p className="chat-rooms-hint">Nadie conectado en este momento.</p>
                    ) : (
                      <ul className="chat-online-list">
                        {onlineUsers.map((u) => {
                          const yaSilenciado = (moderacion?.usuarios_silenciados || []).some((s) => Number(s.id) === Number(u.id));
                          const soyYo = Number(u.id) === Number(currentUserId);
                          const puedeSilenciar = esEquipo && !soyYo && !yaSilenciado;
                          return (
                            <li key={u.id} className="chat-online-item">
                              <div className="chat-online-avatar">
                                {u.avatar_url ? (
                                  <img src={u.avatar_url} alt="" />
                                ) : (
                                  <span className="chat-online-avatar-placeholder">{(u.nombre || u.username || '?').charAt(0).toUpperCase()}</span>
                                )}
                              </div>
                              <span className="chat-online-name">{u.nombre || u.username}</span>
                              {yaSilenciado && <span className="chat-online-silenciado-badge">Silenciado</span>}
                              {puedeSilenciar && (
                                <button
                                  type="button"
                                  className="chat-online-btn-silenciar"
                                  onClick={() => handleSilenciarUsuario(u.id)}
                                >
                                  Silenciar
                                </button>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="chat-messages">
              {loadingMessages ? (
                <p className="chat-rooms-hint">Cargando mensajes…</p>
              ) : (
                <>
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`chat-message ${msg.system ? 'chat-message--system' : ''}`}
                    >
                      <div className="chat-message-head">
                        {!msg.system && (
                          <span className="chat-message-user">
                            {msg.user}
                            {msg.rol && <span className="chat-message-rol">{msg.rol}</span>}
                          </span>
                        )}
                        {esEquipo && !msg.system && (
                          <button
                            type="button"
                            className="chat-message-btn-ocultar"
                            onClick={() => handleOcultarMensaje(msg.id)}
                            title="Ocultar mensaje"
                          >
                            Borrar
                          </button>
                        )}
                      </div>
                      <p className="chat-message-text">{msg.text}</p>
                      <span className="chat-message-time">
                        {msg.datetime || msg.time}
                      </span>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            <form className="chat-input-wrap" onSubmit={handleSend}>
              <input
                type="text"
                className="chat-input"
                placeholder={puedoEscribir ? 'Escribe un mensaje…' : (soloEquipoPuedeEscribir ? 'Solo el equipo puede escribir' : 'No podés escribir en esta sala')}
                value={messageDraft}
                onChange={(e) => setMessageDraft(e.target.value)}
                maxLength={CHAT_MENSAJE_MAX_CHARS}
                disabled={sending || !puedoEscribir}
                aria-label="Mensaje"
              />
              <button type="submit" className="chat-send" disabled={sending || !messageDraft.trim() || !puedoEscribir}>
                {sending ? 'Enviando…' : 'Enviar'}
              </button>
            </form>
          </>
        ) : (
          <div className="chat-main-empty">
            {loadingSalas ? (
              <p>Cargando…</p>
            ) : salas.length === 0 ? (
              <p>No hay salas disponibles.</p>
            ) : (
              <p>Selecciona una sala a la izquierda.</p>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default Chat;
